/**
 * Cloud playlist track resolution — shapes user-library-service playlist rows
 * into the player/display format and resolves each row to a LOCAL file where
 * possible. Shared by the playlist sidebar and the local-file tools'
 * playlist pickers (Compatibility / Loudness / Organize / Fix Metadata /
 * Cue Points).
 *
 * Resolution order per cloud row (see CloudLinkService in the backend):
 *   1. cloud_track_links (server row id → local track id) — exact, survives
 *      file moves because local rows keep their path current.
 *   2. Server-stored origin file_path → local tracks natural key (seeds the
 *      link for next time).
 *   3. LEGACY one-time backfill: artist/title heuristic search. On a match
 *      the link is persisted locally AND the origin path is PATCHed to the
 *      server (fill-if-NULL), so subsequent loads take path 1 — the
 *      heuristic never runs twice for the same row.
 */
import { resolveCover } from './cover';
import { resolveMixCover, firstTrackCover } from './nativeMixCover';
import { appleArtworkOnly } from './webOnly';
import { cleanDisplayTitle } from '../hooks/useEnrichedTracks';
import { refreshAndRetry } from '../redux/utils/refreshAndRetry';

const isDesktop = typeof window !== 'undefined' && !!window.electron?.db;

/**
 * Build a /lazy-audio musicSrc for a streaming row — the audio server tries
 * full playback at PLAY time (stored YT/SC link, else the lazy YT search for
 * rows with a catalog id) and 302s to the Apple sample only on a miss. Same
 * entry shape TrackContextMenu / FeedTab queue. Returns null when the row has
 * nothing the endpoint could resolve (no link, no id).
 */
export function buildLazyAudioSrc({ youtubeId, soundcloudUrl, trackPublicId, artist, title, duration, sample }) {
    const ytId = youtubeId && youtubeId !== '-1' ? youtubeId : null;
    const canSearch = trackPublicId && (artist || title);
    const q = ytId ? `yt=${encodeURIComponent(ytId)}`
        : soundcloudUrl ? `sc=${encodeURIComponent(soundcloudUrl)}`
        : canSearch
            ? `tid=${encodeURIComponent(trackPublicId)}&artist=${encodeURIComponent(artist || '')}&title=${encodeURIComponent(title || '')}`
                + (duration ? `&dur=${encodeURIComponent(duration)}` : '')
                + (() => { try { const t = localStorage.getItem('djpal:token'); return t ? `&tok=${encodeURIComponent(t)}` : ''; } catch { return ''; } })()
            : null;
    if (!q) return null;
    const fb = sample ? `&fallback=${encodeURIComponent(sample)}` : '';
    return {
        musicSrc: `http://localhost:3069/lazy-audio?${q}${fb}`,
        originalUrl: ytId ? `https://www.youtube.com/watch?v=${ytId}` : (soundcloudUrl || null),
    };
}

// artist|title → catalog hit (or null) — memoized so repeat clicks on the
// same legacy row don't re-search. Negative results are cached too.
const catalogMatchCache = new Map();

// Native-legal cover for a MIX playlist row. The capacitor shim nulls every
// non-Apple cover host in API responses (iOS/Android may only display Apple
// Music artwork), so a mix row added on desktop — whose stored artwork is the
// mix's scraped cover — reaches native with no artwork at all. Fetch the mix
// once (lite) and let resolveMixCover pick what every native mix surface
// shows: the first tracklist track's Apple artwork. Memoized per mix id,
// misses included.
const nativeMixCoverCache = new Map();
async function nativeCoverForMix(mixPid) {
    if (nativeMixCoverCache.has(mixPid)) return nativeMixCoverCache.get(mixPid);
    let cover = null;
    try {
        const token = (() => { try { return localStorage.getItem('djpal:token'); } catch { return null; } })();
        const search = window.electron?.djpal?.searchMixesLite || window.electron?.djpal?.searchMixes;
        if (token && search) {
            const res = await search(token, { public_id: mixPid }, null, { page: 1, limit: 1 });
            const data = res?.data || res;
            const mix = (data?.results || data?.result || [])[0] || null;
            // off native (the flag forced on for web/desktop) resolveMixCover returns the stripped original
            cover = resolveMixCover(mix) || firstTrackCover(mix) || null;
        }
    } catch (_) { /* placeholder stands */ }
    nativeMixCoverCache.set(mixPid, cover);
    return cover;
}

/**
 * Match a legacy playlist row (no catalog id, no YT/SC link — pre-2026 adds
 * only stored artist/title/sample) to the Mixpedia catalog by name, so its
 * play can upgrade to the full-playback /lazy-audio path. ONE bounded search
 * per distinct row; conservative accept (normalized title match + primary
 * artist present) — a wrong match would play the wrong track.
 */
export async function matchCatalogTrack({ artist, title }) {
    const a = (artist || '').trim();
    const t = (title || '').trim();
    if (!t || !isDesktop || !window.electron?.djpal?.searchTracks) return null;
    const key = `${a.toLowerCase()}|||${t.toLowerCase()}`;
    if (catalogMatchCache.has(key)) return catalogMatchCache.get(key);
    let hit = null;
    try {
        const token = (() => { try { return localStorage.getItem('djpal:token'); } catch { return null; } })();
        if (token) {
            const cleanTitle = cleanDisplayTitle(t).trim() || t;
            const primaryArtist = a.split(/,|&|\bfeat\.?\b/i)[0].trim();
            const res = await window.electron.djpal.searchTracks(token, `${primaryArtist} ${cleanTitle}`.trim(), 1, 5);
            const norm = (s) => (s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
            const wantTitle = norm(cleanTitle);
            const wantArtist = norm(primaryArtist);
            const found = (res?.results || []).find(r =>
                norm(r.title) === wantTitle
                && (!wantArtist || norm(r.artist).includes(wantArtist))
            ) || null;
            // searchTracks returns the catalog public id as `id` — normalize to
            // `publicId` so callers can't silently read `undefined` (they did,
            // which dropped the tid= lazy-search fallback and the like button's
            // djpalId for every row matched this way).
            if (found) hit = { ...found, publicId: found.publicId || found.id || null };
        }
    } catch (_) { /* search hiccup — treat as no match, cache below */ }
    catalogMatchCache.set(key, hit);
    return hit;
}

// A row the cloud playlist carries by NAME only — pre-2026 adds stored artist,
// title and (sometimes) an Apple sample, no catalog id and no YT/SC link. These
// are the rows that need the catalog match before they can play in full.
const isLegacyRow = (t) => !!t
    && !t._localPath && !t.filePath
    && !t.youtubeId && !t.soundcloudUrl && !t.track_public_id
    // A MIX row is not an unresolved track — matching it against the TRACK
    // catalog by name would graft a wrong track identity onto it.
    && !t.mixPublicId && !t.mix_public_id;

/**
 * Upgrade ONE legacy row to the full-playback /lazy-audio path via a one-time
 * (memoized) catalog match. Returns the row untouched when it isn't legacy or
 * nothing matched. A matched row also loses `missingLocal` — the sample-less
 * rows flagged unplayable by shapeServerTracks become playable from YouTube.
 */
export async function upgradeLegacyRow(track, { playlistId, accessToken } = {}) {
    if (!isDesktop || !isLegacyRow(track)) return track;
    const artist = track.singer || track.artist || '';
    const title = track.name || track.title || '';
    const hit = await matchCatalogTrack({ artist, title });
    if (!hit) return track;
    // Persist the identity we just found onto the server row (fill-if-NULL).
    // This is what keeps the name search a ONE-TIME bootstrap: the next load
    // of this playlist reads track_public_id / youtube_id straight off the row
    // and resolves by id, exactly like a modern add. Fire-and-forget — this
    // play already has everything it needs.
    if (playlistId && track.id && accessToken && window.electron?.library?.setPlaylistTrackCatalog) {
        try {
            refreshAndRetry((token) => window.electron.library.setPlaylistTrackCatalog(token, playlistId, track.id, {
                trackPublicId: hit.publicId || null,
                youtubeId: hit.youtubeId && hit.youtubeId !== '-1' ? hit.youtubeId : null,
                soundcloudUrl: hit.soundcloudUrl || null,
                previewUrl: track.samplePreviewUrl || hit.sampleUrl || null,
            })).catch(() => {});
        } catch (_) { /* best-effort */ }
    }
    const lazy = buildLazyAudioSrc({
        youtubeId: hit.youtubeId,
        soundcloudUrl: hit.soundcloudUrl,
        trackPublicId: hit.publicId,
        artist,
        title,
        duration: track.duration,
        sample: track.samplePreviewUrl || hit.sampleUrl || '',
    });
    if (!lazy) return track;
    return {
        ...track,
        musicSrc: lazy.musicSrc,
        missingLocal: false,
        ...(lazy.originalUrl ? { originalUrl: lazy.originalUrl } : {}),
        youtubeId: hit.youtubeId && hit.youtubeId !== '-1' ? hit.youtubeId : track.youtubeId,
        soundcloudUrl: hit.soundcloudUrl || track.soundcloudUrl,
        djpalId: hit.publicId,
        samplePreviewUrl: track.samplePreviewUrl || hit.sampleUrl || null,
    };
}

/**
 * Upgrade a whole queue's worth of legacy rows before it reaches the player.
 * Play All / "load into queue" used to skip this (only a row CLICK ran the
 * match), so those paths queued the 30-second Apple sample for every legacy
 * row while clicking the same row played it in full.
 *
 * Bounded parallelism keeps a 40-row playlist from firing 40 simultaneous
 * searches; matchCatalogTrack's memo (negatives included) makes every repeat
 * free, so re-playing a playlist costs nothing.
 */
export async function upgradeLegacyRows(tracks, { concurrency = 6, playlistId, accessToken, onlyUnplayable = false } = {}) {
    if (!isDesktop || !Array.isArray(tracks) || !tracks.length) return tracks;
    // onlyUnplayable: just the rows that would otherwise render as "not on
    // this device" — no local file, no sample, nothing. Cheap enough to run on
    // every playlist EXPAND so the badge and the hero count tell the truth,
    // without paying a search for rows that already have something to play.
    // (A row with _localPath is genuinely on another machine — leave it.)
    const wants = (t) => isLegacyRow(t) && (!onlyUnplayable || (t.missingLocal && !t._localPath));
    if (!tracks.some(wants)) return tracks;
    const out = tracks.slice();
    let next = 0;
    const worker = async () => {
        while (next < out.length) {
            const i = next++;
            if (!wants(out[i])) continue;
            try {
                out[i] = await upgradeLegacyRow(out[i], { playlistId, accessToken });
            } catch (_) { /* leave the row as-is — it still plays its sample */ }
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, out.length) }, worker));
    return out;
}

/**
 * The rows of a shaped playlist that "Restore from Cloud Backup" can act on:
 * flagged missing on this machine AND carrying a known on-disk path (from
 * cloud_track_links / the server origin path). Rows with no path at all have
 * nothing to look up in the backup. Returns backup:restore:files items.
 */
export function restorableBackupItems(tracks) {
    return (tracks || [])
        .filter((t) => (t?.missingLocal || t?.offDevice) && t._localPath && !/^(online|https?|youtube):\/\//.test(t._localPath))
        .map((t) => ({
            filePath: t._localPath,
            fileName: t._localPath.split(/[\\/]/).pop(),
        }));
}

/**
 * Best-effort local path for a track object in any of the app's row shapes.
 * Falls back to decoding the local audio-server URL in musicSrc.
 */
export function localPathFromTrack(track) {
    if (!track) return null;
    const direct = track.filePath || track.file || track._localPath;
    if (direct && !/^(online|https?|youtube):\/\//.test(direct)) return direct;
    const src = track.musicSrc || '';
    if (src.startsWith('http://localhost:3069/audio')) {
        try {
            return new URL(src).searchParams.get('path') || null;
        } catch (_) { /* fall through */ }
    }
    return null;
}

/**
 * Shape server-playlist rows into the player/display format, resolving local
 * files exactly via cloud_track_links / origin path, with a one-time
 * heuristic backfill for legacy rows. Flags rows whose resolved file isn't
 * on this machine (missingLocal).
 *
 * @param {object} playlistData - GET /playlists/:id payload ({id, name, tracks})
 * @param {object} [opts]
 * @param {string} [opts.accessToken] - enables the server-side origin-path
 *   backfill PATCH for heuristic matches (fire-and-forget).
 */
export async function shapeServerTracks(playlistData, { accessToken } = {}) {
    const rawTracks = playlistData?.tracks || [];

    // Pass 1 — exact resolution: link table first, then server origin path.
    // One IPC round-trip for the whole playlist.
    let resolved = {};
    if (isDesktop && rawTracks.length && window.electron?.db?.cloudLinks?.resolve) {
        try {
            resolved = await window.electron.db.cloudLinks.resolve(
                rawTracks.map((t) => ({ serverRowId: t.id, filePath: t.file_path || null }))
            ) || {};
        } catch (_) { /* fall through to heuristic */ }
    }

    const tracks = await Promise.all(rawTracks.map(async (t) => {
        let musicSrc = t.preview_url || t.soundcloud_url || '';
        // Mixes don't play off-desktop. A mix row that happens to have kept a
        // SoundCloud link or a stored sample would otherwise become playable
        // while the mix sitting next to it — same kind of row, no leftover
        // link — stays inert: the play affordance ends up decided by which
        // link survived the add, not by what the row IS. Drop the src so every
        // mix row is uniformly non-playable here (and stays out of the queue
        // builders, which gate on musicSrc). Desktop is untouched — it plays
        // mixes through the mix player, not this field.
        if (t.mix_public_id && !isDesktop) musicSrc = '';
        let localPath = resolved[t.id]?.filePath || null;
        // No link and no local DB row, but the server knows the origin path:
        // trust it when it's a real filesystem path — the checkMissingFiles
        // batch below drops it (missingLocal + filePath cleared) if the file
        // isn't actually on this disk. Covers a fresh install / second app
        // instance whose local DB never indexed the library: the files are
        // there, only the DB rows aren't — and the tools only need paths.
        if (!localPath && isDesktop && t.file_path && !/^(online|https?|youtube):\/\//.test(t.file_path)) {
            localPath = t.file_path;
        }
        if (localPath) {
            musicSrc = `http://localhost:3069/audio?path=${encodeURIComponent(localPath)}`;
        }
        // Legacy backfill — row has no link and no usable origin path. Look
        // up by artist+title ONCE, then persist the link on both sides so
        // this branch never runs again for the row. Mix rows are excluded:
        // a mix title matched against the local TRACKS table would link a
        // wrong file.
        if (!localPath && !musicSrc && isDesktop && !t.mix_public_id && (t.artist || t.title)) {
            try {
                // ONE lookup per track — expand latency scales with these.
                // Cloud rows from DJ imports often pack everything into the
                // title with a null artist ("Talassa Barda - Wolf (Original
                // Mix) [ClapCrate.net]") while the local tag is split and
                // beautified ("Wolf (Original Mix)"): split the artist
                // prefix off and strip the junk suffix BEFORE searching.
                // LIKE %term% matching makes the cleaned query a superset
                // of a raw-title search (the cleaned title / primary artist
                // is a substring of its junky / multi-artist variant), so
                // no second query is needed.
                let lookupArtist = (t.artist || '').trim();
                let lookupTitle = (t.title || '').trim();
                if (!lookupArtist) {
                    const m = lookupTitle.match(/^(.+?)\s+-\s+(.+)$/);
                    if (m) {
                        lookupArtist = m[1].trim().replace(/^\d+[-.\s]+/, '').trim();
                        lookupTitle = m[2].trim();
                    }
                }
                lookupTitle = cleanDisplayTitle(lookupTitle).trim() || lookupTitle;
                const primaryArtist = lookupArtist.split(/,|&|\bfeat\.?\b/i)[0].trim();
                if (lookupTitle) {
                    const found = await window.electron.db.tracks.search({
                        ...(primaryArtist ? { artist: primaryArtist } : {}),
                        title: lookupTitle,
                        limit: 3,
                    });
                    const match = found?.[0];
                    if (match?.file) {
                        localPath = match.file;
                        musicSrc = `http://localhost:3069/audio?path=${encodeURIComponent(match.file)}`;
                        // Persist the link so the heuristic never re-runs:
                        // locally (exact id link) + server origin path
                        // (fill-if-NULL). Both fire-and-forget — resolution
                        // for THIS render already succeeded.
                        if (t.id) {
                            window.electron.db.cloudLinks?.setByPath?.(t.id, match.file)?.catch?.(() => {});
                            if (accessToken && playlistData?.id && window.electron.library?.setPlaylistTrackFilePath) {
                                refreshAndRetry((token) => window.electron.library.setPlaylistTrackFilePath(token, playlistData.id, t.id, match.file)).catch(() => {});
                            }
                        }
                    }
                }
            } catch (_) {}
        }
        // Normalize to the player/display shape used everywhere else
        // ({ name: title, singer: artist }) so the row shows the artist
        // on its own line instead of a single "Artist - Title" string
        // (which rendered "undefined - Title" when the artist was
        // missing). When the artist is absent but the title carries
        // "Artist - Title", split it so the artist still shows.
        let displayTitle = (t.title || '').trim();
        let displayArtist = (t.artist || '').trim();
        if (displayArtist && displayTitle.toLowerCase().startsWith(`${displayArtist.toLowerCase()} - `)) {
            // Title redundantly repeats "Artist - " (legacy imports) — strip it.
            displayTitle = displayTitle.slice(displayArtist.length + 3).trim();
        } else if (!displayArtist && displayTitle) {
            const m = displayTitle.match(/^(.+?)\s+-\s+(.+)$/);
            if (m) {
                displayArtist = m[1].trim().replace(/^\d+[-.\s]+/, '').trim();
                displayTitle = m[2].trim();
            }
        }
        // Desktop streaming rows: don't let the Apple sample short-circuit
        // full playback (clicking a row used to play the 30s sample with no
        // full-stream lookup at all). Route through /lazy-audio — full
        // stream first, sample only as the endpoint's fallback. Rows with
        // neither a link nor a catalog id keep the plain sample; the click
        // path tries a one-time catalog match for those (matchCatalogTrack).
        // Web/mobile are samples-only platforms — they keep the direct URL.
        let originalUrl = null;
        if (!localPath && isDesktop) {
            const lazy = buildLazyAudioSrc({
                youtubeId: t.youtube_id,
                soundcloudUrl: t.soundcloud_url,
                trackPublicId: t.track_public_id,
                artist: displayArtist,
                title: displayTitle,
                duration: t.duration,
                sample: t.preview_url || '',
            });
            if (lazy) {
                musicSrc = lazy.musicSrc;
                originalUrl = lazy.originalUrl;
            }
        }
        return {
            ...t,
            name: displayTitle || displayArtist || 'Unknown',
            singer: displayArtist,
            musicSrc,
            ...(originalUrl ? { originalUrl } : {}),
            // Real sample URL (matching + save-queue must not persist the
            // machine-specific localhost /lazy-audio URL).
            samplePreviewUrl: t.preview_url || null,
            cover: resolveCover(t.artwork),
            _localPath: localPath,
            // Canonical on-disk path key (TrackContextMenu gates its
            // "Show in Finder" item on this). Cleared below for files
            // missing on this machine.
            filePath: localPath,
            // Play links for src-less rows (no sample, no local file):
            // resolvable on click via yt-dlp / lazy YT resolve.
            youtubeId: (t.youtube_id && t.youtube_id !== '-1' ? t.youtube_id : null),
            soundcloudUrl: t.soundcloud_url || null,
            // Mix rows carry their own identity — clicks open the MIX page
            // id-first instead of running a track search that can't succeed.
            mixPublicId: t.mix_public_id || null,
        };
    }));
    // A cloud playlist row resolved to a LOCAL file may point at a path
    // that only exists on the machine the playlist was synced from (the
    // tracks DB row synced across, the audio didn't). Verify in one
    // batch and set missingLocal so the "not on this device" badge and
    // the queue filters work for server playlists too.
    const localPaths = tracks.map(t => t._localPath).filter(Boolean);
    if (localPaths.length && window.electron?.db?.tracks?.checkMissingFiles) {
        try {
            const missing = new Set(await window.electron.db.tracks.checkMissingFiles(localPaths));
            for (const t of tracks) {
                if (t._localPath && missing.has(t._localPath)) {
                    // Finder can't reveal an absent file — keep the menu honest.
                    t.filePath = null;
                    // The file is on another computer, but a row that also
                    // carries a stream link / catalog id plays from that
                    // instead of being a dead 404 in the queue. offDevice
                    // keeps it in "Restore from Cloud Backup".
                    const lazy = isDesktop && !t.mix_public_id ? buildLazyAudioSrc({
                        youtubeId: t.youtube_id,
                        soundcloudUrl: t.soundcloud_url,
                        trackPublicId: t.track_public_id,
                        artist: t.singer,
                        title: t.name,
                        duration: t.duration,
                        sample: t.preview_url || '',
                    }) : null;
                    if (lazy) {
                        t.musicSrc = lazy.musicSrc;
                        if (lazy.originalUrl) t.originalUrl = lazy.originalUrl;
                        t.offDevice = true;
                    } else {
                        t.missingLocal = true;
                    }
                }
            }
        } catch (_) { /* check failed — leave unflagged */ }
    }
    // Rows persisted before adds read file tags carry a filename-guessed
    // identity (artist "12" from "12 - Ain't It Fun.flac"). The server row
    // can't be renamed (no endpoint), but when the file is right here we can
    // read its real tags and heal the DISPLAY — and, via these fields,
    // everything downstream (queue entries, Search in Mixpedia query). Only
    // clearly-guessed identities are re-read so expand latency doesn't grow
    // with playlist size: no artist, a bare track-number artist, or the
    // 'Unknown Artist' filler. Catalog-resolved rows are never touched.
    const guessedIdentity = (t) => {
        const a = (t.singer || '').trim();
        return !a || /^\d+$/.test(a) || a.toLowerCase() === 'unknown artist';
    };
    const suspect = tracks.filter((t) => t._localPath && !t.missingLocal && !t.track_public_id && guessedIdentity(t));
    if (suspect.length && window.electron?.metadata?.readTags) {
        try {
            const tags = await window.electron.metadata.readTags(suspect.map((t) => t._localPath));
            for (const t of suspect) {
                const tag = tags?.[t._localPath];
                if (!tag) continue; // file carries no tags — the guess stands
                t.name = cleanDisplayTitle(tag.title) || tag.title;
                t.singer = tag.artist;
                // Raw fields too — adaptTrackForMixpedia and the add-to-playlist
                // flows read title/artist before name/singer.
                t.title = tag.title;
                t.artist = tag.artist;
            }
        } catch (_) { /* tag read failed — filename guess stands */ }
    }
    // A track with no source at all on this machine (no preview URL, no
    // stream link, no local file match, no music-info id to lazy-resolve
    // by) can't play here — flag it for the badge/dimming/queue-filter.
    // Tracks with a YT/SC link or a public id are NOT flagged: they
    // resolve on click (yt-dlp / lazy YT search) — the common case for
    // no-artwork tracks, which have no Apple sample but play fine from
    // YouTube. Mix rows aren't flagged either: their mix id fetches the
    // mix (with its play link) on click — a Mixcloud mix stores no YT/SC
    // link at add time yet plays fine. Desktop only: on web the "Desktop
    // only" badge covers it.
    if (isDesktop) {
        for (const t of tracks) {
            if (!t.musicSrc && !t.youtubeId && !t.soundcloudUrl && !t.track_public_id && !t.mix_public_id) t.missingLocal = true;
        }
    }
    // Apple-only artwork: mix rows whose artwork the compliance filter nulled (scraped,
    // non-Apple cover hosts) re-resolve their cover from the mix itself —
    // typically one or two rows per playlist, memoized across expands.
    if (appleArtworkOnly()) {
        await Promise.all(tracks.map(async (t) => {
            if (!t.mix_public_id || t.artwork) return;
            const cover = await nativeCoverForMix(t.mix_public_id);
            if (cover) t.cover = cover;
        }));
    }
    return tracks;
}

/**
 * Load a cloud playlist and reduce it to the on-disk file paths the
 * local-file tools can operate on.
 *
 * @returns {Promise<{name:string, filePaths:string[], total:number, rowIdsByPath:Object}|null>}
 *   null when the playlist couldn't be loaded at all. rowIdsByPath maps each
 *   resolved local path to its user_playlist_tracks row id, so callers that
 *   produce a replacement file (Compatibility fix) can repoint the row's
 *   local link afterwards.
 */
export async function loadCloudPlaylistFiles(accessToken, playlistId) {
    // refreshAndRetry: a stale token (cold start) refreshes + retries
    // instead of surfacing as "playlist not found".
    const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, playlistId));
    const playlistData = result?.data || result;
    if (!playlistData || playlistData.error) return null;
    const shaped = await shapeServerTracks(playlistData, { accessToken });
    const withLocal = shaped.filter((t) => t.filePath && !t.missingLocal);
    const rowIdsByPath = {};
    for (const t of withLocal) {
        if (t.id) rowIdsByPath[t.filePath] = t.id;
    }
    return {
        name: playlistData.name,
        filePaths: withLocal.map((t) => t.filePath),
        rowIdsByPath,
        total: shaped.length,
    };
}
